import type { SessionStatus } from './VoiceAgentController'

export type ArcturianAvatarState =
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'pleased'
  | 'concerned'
  | 'error'
  | 'greeting'

export type ArcturianOverlayIndicator =
  | 'none'
  | 'mic_level'
  | 'activity_pulse'
  | 'error'

export type ArcturianAvatarAssetRole =
  | 'idle'
  | 'talk_short'
  | 'talk_medium'
  | 'talk_long'
  | 'happy'
  | 'joyful'
  | 'sad'
  | 'greeting'

export interface ArcturianAvatarAsset {
  role: ArcturianAvatarAssetRole
  state: ArcturianAvatarState
  url: string
  poster: string
  loop: boolean
  duration: number
  fallback_state: 'idle'
  overlay_indicator: ArcturianOverlayIndicator
  sha256: string
}

export interface ArcturianAvatarStateDefinition {
  assets: readonly ArcturianAvatarAssetRole[]
  fallback_state: 'idle'
  overlay_indicator: ArcturianOverlayIndicator
}

const STORAGE_MEDIA_BASE = 'https://api-storage.arkturian.com/storage/media'
const IDLE_SHA = 'bad739abf509b0a011826f3da58aef46a92cf1237cdc9e49aadcbfdf3559106f'
const IDLE_POSTER = `${STORAGE_MEDIA_BASE}/114355?width=720&height=720&format=jpg&v=${IDLE_SHA}`

/**
 * Revision-pinned presentation contract for the first Arcturian delivery.
 *
 * The eight source videos are the verified Dr.-Tschauko clips. They are muted
 * H.264 MP4s without alpha. Listening, thinking and error intentionally reuse
 * the neutral idle visual and become distinguishable through explicit overlays;
 * no emotion is inferred from spoken words.
 */
export const ARCTURIAN_AVATAR_MANIFEST = {
  character_id: 'arcturian.dr-peter-tschauko',
  manifest_revision: '2026-08-03.2',
  renderer_contract: {
    first_frame_gate: true,
    failure_behavior: 'keep_outgoing_or_poster',
    web: {
      crossfade_ms: 220,
      easing: 'linear',
      persistent_video_elements: 2,
    },
    ios: {
      crossfade_ms: 420,
      easing: 'smoothstep',
      steady_state_decoders: 1,
      transition_decoders: 2,
    },
  },
  states: {
    idle: { assets: ['idle'], fallback_state: 'idle', overlay_indicator: 'none' },
    listening: { assets: ['idle'], fallback_state: 'idle', overlay_indicator: 'mic_level' },
    thinking: { assets: ['idle'], fallback_state: 'idle', overlay_indicator: 'activity_pulse' },
    speaking: {
      assets: ['talk_short', 'talk_medium', 'talk_long'],
      fallback_state: 'idle',
      overlay_indicator: 'none',
    },
    pleased: {
      assets: ['happy', 'joyful'],
      fallback_state: 'idle',
      overlay_indicator: 'none',
    },
    concerned: { assets: ['sad'], fallback_state: 'idle', overlay_indicator: 'none' },
    error: { assets: [], fallback_state: 'idle', overlay_indicator: 'error' },
    greeting: { assets: ['greeting'], fallback_state: 'idle', overlay_indicator: 'none' },
  } satisfies Record<ArcturianAvatarState, ArcturianAvatarStateDefinition>,
  assets: {
    idle: {
      role: 'idle',
      state: 'idle',
      url: `${STORAGE_MEDIA_BASE}/114355?v=${IDLE_SHA}`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 5.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: IDLE_SHA,
    },
    talk_short: {
      role: 'talk_short',
      state: 'speaking',
      url: `${STORAGE_MEDIA_BASE}/114356?v=172723741cb02c5b11c64200903afcdaea4872f8b37642694a0b882f909cf1b9`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 4.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: '172723741cb02c5b11c64200903afcdaea4872f8b37642694a0b882f909cf1b9',
    },
    talk_medium: {
      role: 'talk_medium',
      state: 'speaking',
      url: `${STORAGE_MEDIA_BASE}/114357?v=401348b8223b1b1e6f5e39045c69233cadcc28b5651bd99baf188666bba540bb`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 8.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: '401348b8223b1b1e6f5e39045c69233cadcc28b5651bd99baf188666bba540bb',
    },
    talk_long: {
      role: 'talk_long',
      state: 'speaking',
      url: `${STORAGE_MEDIA_BASE}/114358?v=21e39efb0b2315a638095958709c7601dc17626cf5a7d2198661702e02146ceb`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 12.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: '21e39efb0b2315a638095958709c7601dc17626cf5a7d2198661702e02146ceb',
    },
    happy: {
      role: 'happy',
      state: 'pleased',
      url: `${STORAGE_MEDIA_BASE}/114359?v=120afa4655ac725a0ce06788ae03e6a0a8d1eca67260eb9aae46677a6a5f969e`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 6.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: '120afa4655ac725a0ce06788ae03e6a0a8d1eca67260eb9aae46677a6a5f969e',
    },
    joyful: {
      role: 'joyful',
      state: 'pleased',
      url: `${STORAGE_MEDIA_BASE}/114360?v=f94f9c42182f333db6aa604ab27bb3609173b59e4e61301550009ecedc6f394b`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 6.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: 'f94f9c42182f333db6aa604ab27bb3609173b59e4e61301550009ecedc6f394b',
    },
    sad: {
      role: 'sad',
      state: 'concerned',
      url: `${STORAGE_MEDIA_BASE}/114361?v=9fd532e1231253d7121113c125b34262e578929ac3c7b6520efe97e53398f35c`,
      poster: IDLE_POSTER,
      loop: true,
      duration: 6.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: '9fd532e1231253d7121113c125b34262e578929ac3c7b6520efe97e53398f35c',
    },
    greeting: {
      role: 'greeting',
      state: 'greeting',
      url: `${STORAGE_MEDIA_BASE}/114410?v=5d8db1210d8d0134d4f5c58a7f2f65df0ec8053dcfd9aa4fa948cf1d2a99bace`,
      poster: IDLE_POSTER,
      loop: false,
      duration: 5.042,
      fallback_state: 'idle',
      overlay_indicator: 'none',
      sha256: '5d8db1210d8d0134d4f5c58a7f2f65df0ec8053dcfd9aa4fa948cf1d2a99bace',
    },
  } satisfies Record<ArcturianAvatarAssetRole, ArcturianAvatarAsset>,
} as const

export interface ArcturianAvatarProjectionInput {
  status: SessionStatus
  activeTool?: string | null
  isMicActive?: boolean
  /** Authoritative server projection when the durable Arcturian task emits it. */
  projectedState?: ArcturianAvatarState | null
}

/**
 * Legacy Realtime sessions do not yet emit an avatar projection. This adapter
 * maps their explicit lifecycle events without inspecting transcript text.
 */
export function resolveArcturianAvatarState(input: ArcturianAvatarProjectionInput): ArcturianAvatarState {
  if (input.projectedState) return input.projectedState
  if (input.status === 'error') return 'error'
  if (input.status === 'speaking') return 'speaking'
  if (input.activeTool) return 'thinking'
  if (input.status === 'listening' || input.isMicActive) return 'listening'
  return 'idle'
}

export function overlayForArcturianState(state: ArcturianAvatarState): ArcturianOverlayIndicator {
  return ARCTURIAN_AVATAR_MANIFEST.states[state].overlay_indicator
}

/**
 * A real conversation phase always wins over the decorative entrance gesture.
 * In particular, the first audible frame must never wait for `greeting` to end.
 */
export function shouldPreemptAvatarGreeting(state: ArcturianAvatarState): boolean {
  return state !== 'idle' && state !== 'greeting'
}

export function assetForRole(role: ArcturianAvatarAssetRole): ArcturianAvatarAsset {
  return ARCTURIAN_AVATAR_MANIFEST.assets[role]
}
